import React, { useCallback, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import moment from 'moment';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import Text from '../../components/Text';
import { SendIcon, ViewContactIcon, EditIcon } from '../../components/Icon';
import { authNameE } from '../../utils/userFuncs';
import { SERVICE_TYPE } from '../../comm/constant'
import { i18n } from '../../language';

const PatientInfoCard = ({ data, setSenderContactPopup, setShareSurveyPopup, setEditAppreciationPopup }) => {
  const classes = useStyles();
  const language = 'En';
  // const language = i18n.locale === 'en-us' ? 'En' : 'Zh';
  const roleMap = useSelector((state) => state.roleMap);

  const handleAppreciationNature = (appreciationNature) => {
    try {
      const appreciationNatureIsNull = appreciationNature === null;
      return appreciationNatureIsNull
        ? ''
        : _.map(
          JSON.parse(appreciationNature),
          (item) =>
            `${item.answerSortBy}.${item[`optionDesc${language}`]}${item.answerText ? `-${item.answerText}` : ''}`
        ).join(', ');
    } catch (error) {
      return '';
    }
  };

  const appreciationNature = handleAppreciationNature(data?.appreciationNature);

  const getCommentText = useCallback(
    () => {
      
      
      const text = `${data?.[`title${language}`] ?? ''} ${data?.senderName ?? ''} Appreciation to `

      const serviceTxt = `${data?.[`service${language}Desc`] ?? ''}`;
      const subFieldTxt = `${data?.[`department${language}Desc`] || data.departmentText || ''}`;

      const serviceType = data.serviceType
      switch (serviceType) {
        case SERVICE_TYPE.INPATIENT:
          return text.concat(`${serviceTxt}${subFieldTxt === '' ? '': ' - '}${subFieldTxt} ${data?.[`pecsWard${language}Desc`] ?? ''}`);
        case SERVICE_TYPE.AED:
          return text.concat(`${serviceTxt}`);
        case SERVICE_TYPE.SOPC:
        case SERVICE_TYPE.AHC:
        case SERVICE_TYPE.GOPC:
        case SERVICE_TYPE.OTHERS:
          return text.concat(`${serviceTxt}${subFieldTxt === '' ? '': ' - '}${subFieldTxt}`);
        default:
          return '';
      }
    },
    [data]
  )

  return (
    <Box bgcolor="#fff" boxShadow="0px 7px 16px 0px rgba(0, 0, 0, 0.04)" borderRadius="1rem" overflow="hidden">
      <Box p="1.88rem" borderBottom="solid 1px #E4E7EB">
        <Box display="flex" gridGap="1.19rem">
          <Box className={classes.dateIcon}>
            <div className={classes.headIcon}>{moment(data?.createdAt, 'YYYY-MM-DD HH:mm').format('ddd')}</div>
            <div className={classes.numIcon}>{moment(data?.createdAt, 'YYYY-MM-DD HH:mm').date()}</div>
          </Box>
          <Box>
            <Text fontSize="1rem" color="#2E4469">
              Ref. No #{data?.refNo}
            </Text>
            <Text color="#7D86A9">
              {getCommentText()}
            </Text>
          </Box>
        </Box>
        <Text
          mt="1.8rem"
          mb="1.8rem"
          fontSize="1rem"
          color="#2E4469"
          style={{ wordWrap: 'break-word', whiteSpace: 'pre-line', wordBreak: 'normal' }}
        >
          {data?.[data?.type === 'Appreciation' ? 'appreciateText' : 'feedBackText'] ?? ''}
        </Text>
        {data?.type === 'Appreciation' && (
          <Box pt="1.25rem" borderTop="solid 1px #E4E7EB">
            <Text fontSize="1rem" color="#2E4469" mb="0.63rem">
              Appreciated staff : {data?.appreciateStaff}
            </Text>
            <Text fontSize="1rem" color="#2E4469" mb="0.63rem">
              Nature of Appreciation : {appreciationNature}
            </Text>
            {data?.forwards?.length > 0 && (
              <Text fontSize="1rem" color="#2E4469">
                {`Forwarded To : ${data?.forwards
                  ?.map(
                    (item) =>
                      `${item.sendGroup} @ ${moment(item.createdAt, 'YYYY-MM-DD HH:mm').format(
                        'YYYY-MM-DD HH:mm'
                      )} by ${item.createdBy}${item.corpId ? `(${item.corpId})` : ''}`
                  )
                  ?.join(', ')}`}
              </Text>
            )}
          </Box>
        )}
      </Box>
      <Box display="flex">
        {roleMap?.[authNameE.view_presonal_contact_of_appreciation] && (
          <ButtonBase
            className={classes.button}
            style={{ borderRight: 'solid 1px #E4E7EB' }}
            onClick={() => setSenderContactPopup({ open: true, responseDetailId: data?.responseDetailId })}
          >
            <ViewContactIcon className={classes.icon} />
            View Contact
          </ButtonBase>
        )}
        {(roleMap?.[authNameE.view_and_share_all_wards_appreciation] || roleMap?.[authNameE.share_appreciation]) && (
          <ButtonBase
            className={classes.button}
            onClick={() => setShareSurveyPopup({ open: true, responseId: data?.responseId })}
            disabled={!data?.[`pecsWard${language}Desc`]}
          >
            <SendIcon className={classes.icon} />
            Share to {i18n.t('EHANDOVER_EHANDOVER')} and HA Chat
          </ButtonBase>
        )}
        {roleMap?.[authNameE.edit_appreciation] && ( // Only PRO Admin get Edit access right
          <ButtonBase
            className={classes.button}
            onClick={() => setEditAppreciationPopup({ open: true, data })}
          >
            <EditIcon className={classes.icon} />
            Edit Appreciation
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  dateIcon: {
    borderRadius: '0.5rem',
    border: '0.0625rem solid #e4e7eb',
  },
  headIcon: {
    width: '2.55rem',
    height: '0.95rem',
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.625rem',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#538fff',
    borderRadius: '0.468rem 0.468rem 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numIcon: {
    width: '2.55rem',
    height: '1.65rem',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.125rem',
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#001d63',
    backgroundColor: '#fff',
    borderRadius: '0 0 0.468rem 0.468rem',
  },
  button: {
    flexBasis: '100%',
    height: '4.4375rem',
    fontSize: '1rem',
    color: '#001D63',
    fontFamily: 'NotoSans-Medium',
    '&:disabled': {
      backgroundColor: '#c0c6cb',
      opacity: '0.5',
    },
  },
  icon: {
    fontSize: '1.5rem',
    color: '#538fff',
    marginRight: '0.5rem',
  },
}));

export default PatientInfoCard;
